import React, { useState } from 'react';
import { Button, Form, Header, Segment, Tab, Table } from 'semantic-ui-react';
import { API, isBD, showError, timestamp2string } from '../../helpers';
import { renderQuota } from '../../helpers/render';

function formatTokenCount(n) {
  return n > 1000 ? (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K' : n;
}

const now = new Date();
const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());

// tenantMode=true 时走租户管理员只读接口，租户由服务端按绑定自动限定；
// 市场角色走全量只读接口。
const ConsumptionSummary = ({ tenantMode = false }) => {
  const apiBase = tenantMode ? '/api/tenant_admin' : isBD() ? '/api/bd' : '/api/log/admin';
  const [inputs, setInputs] = useState({
    start_timestamp: timestamp2string(todayStart.getTime() / 1000),
    end_timestamp: timestamp2string(now.getTime() / 1000),
    model_name: '',
    username: '',
    channel: '',
  });
  const [stats, setStats] = useState([]);
  const [topupSummary, setTopupSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [groupBy, setGroupBy] = useState('model');
  const [hasQueried, setHasQueried] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingTopup, setExportingTopup] = useState(false);

  const groupTabs = ['model', 'user', 'channel'];
  const activeTabIndex = Math.max(groupTabs.indexOf(groupBy), 0);
  const isUserView = groupBy === 'user';
  const isChannelView = groupBy === 'channel';
  const groupLabel = isUserView ? '用户' : isChannelView ? '渠道' : '模型';

  const handleChange = (e, { name, value }) =>
    setInputs((prev) => ({ ...prev, [name]: value }));

  const buildParams = (groupByValue = groupBy) => {
    const startTs = Math.floor(Date.parse(inputs.start_timestamp) / 1000);
    const endTs = Math.floor(Date.parse(inputs.end_timestamp) / 1000);
    return new URLSearchParams({
      start_timestamp: startTs,
      end_timestamp: endTs,
      model_name: inputs.model_name,
      username: inputs.username,
      channel: inputs.channel,
      group_by: groupByValue,
    }).toString();
  };

  const loadSummary = async (groupByValue = groupBy) => {
    setLoading(true);
    const params = buildParams(groupByValue);
    const [res, topupRes] = await Promise.all([
      API.get(`${apiBase}/summary?${params}`),
      API.get(`${apiBase}/topup_summary?${params}`),
    ]);
    const { success, message, data } = res.data;
    if (success) {
      setStats(data);
    } else {
      showError(message);
    }
    const topupData = topupRes.data;
    if (topupData.success) {
      setTopupSummary(topupData.data);
    }
    setHasQueried(true);
    setLoading(false);
  };

  const handleTabChange = (e, { activeIndex }) => {
    const nextGroupBy = groupTabs[activeIndex] || 'model';
    if (nextGroupBy === groupBy) {
      return;
    }
    setGroupBy(nextGroupBy);
    setStats([]);
    if (hasQueried) {
      loadSummary(nextGroupBy);
    }
  };

  const handleExport = async () => {
    if (!inputs.model_name.trim()) {
      showError('导出时必须指定模型名称');
      return;
    }
    const startTs = Math.floor(Date.parse(inputs.start_timestamp) / 1000);
    const endTs = Math.floor(Date.parse(inputs.end_timestamp) / 1000);
    if (!startTs || !endTs || endTs <= startTs) {
      showError('请选择有效的时间范围');
      return;
    }
    if (endTs - startTs > 90 * 24 * 3600) {
      showError('时间范围不能超过 90 天');
      return;
    }
    setExporting(true);
    try {
      const res = await API.get(`${apiBase}/export?${buildParams()}`, {
        responseType: 'blob',
      });
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename=([^\s;]+)/);
      const filename = match ? match[1] : 'admin-mlog.csv';
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      showError('导出失败：' + (e.message || '未知错误'));
    }
    setExporting(false);
  };

  const handleExportTopup = async () => {
    const startTs = Math.floor(Date.parse(inputs.start_timestamp) / 1000);
    const endTs = Math.floor(Date.parse(inputs.end_timestamp) / 1000);
    if (!startTs || !endTs || endTs <= startTs) {
      showError('请选择有效的时间范围');
      return;
    }
    if (endTs - startTs > 90 * 24 * 3600) {
      showError('时间范围不能超过 90 天');
      return;
    }
    setExportingTopup(true);
    try {
      const res = await API.get(`${apiBase}/export_topup?${buildParams()}`, {
        responseType: 'blob',
      });
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename=([^\s;]+)/);
      const filename = match ? match[1] : 'admin-topup.csv';
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      showError('导出失败：' + (e.message || '未知错误'));
    }
    setExportingTopup(false);
  };

  const totalQuota = stats.reduce((s, r) => s + r.quota, 0);
  const totalRequests = stats.reduce((s, r) => s + r.request_count, 0);
  const totalPrompt = stats.reduce((s, r) => s + r.prompt_tokens, 0);
  const totalCompletion = stats.reduce((s, r) => s + r.completion_tokens, 0);

  return (
    <Segment>
      <Header as='h3'>消费汇总</Header>
      <Form>
        <Form.Group>
          <Form.Input fluid label='起始时间' width={4} type='datetime-local'
            name='start_timestamp' value={inputs.start_timestamp} onChange={handleChange} />
          <Form.Input fluid label='结束时间' width={4} type='datetime-local'
            name='end_timestamp' value={inputs.end_timestamp} onChange={handleChange} />
          <Form.Input fluid label='模型名称' width={3} placeholder='可选'
            name='model_name' value={inputs.model_name} onChange={handleChange} />
          <Form.Input fluid label='用户名称' width={3} placeholder='可选'
            name='username' value={inputs.username} onChange={handleChange} />
          <Form.Input fluid label='渠道 ID' width={2} placeholder='可选'
            name='channel' value={inputs.channel} onChange={handleChange} />
          <Form.Button fluid label='操作' width={2} onClick={() => loadSummary()} loading={loading}>查询</Form.Button>
        </Form.Group>
      </Form>

      <Tab
        menu={{ secondary: true, pointing: true }}
        activeIndex={activeTabIndex}
        onTabChange={handleTabChange}
        panes={[
          {
            menuItem: '按模型',
            render: () => (
              <Tab.Pane attached={false}>
                {renderSummaryTable()}
              </Tab.Pane>
            ),
          },
          {
            menuItem: '按用户',
            render: () => (
              <Tab.Pane attached={false}>
                {renderSummaryTable()}
              </Tab.Pane>
            ),
          },
          {
            menuItem: '按渠道',
            render: () => (
              <Tab.Pane attached={false}>
                {renderSummaryTable()}
              </Tab.Pane>
            ),
          },
        ]}
      />

      {stats.length > 0 && (
        <>
          <Button size='small' loading={exporting} onClick={handleExport}>
            导出明细日志
          </Button>
          <Button size='small' loading={exportingTopup} onClick={handleExportTopup}>
            导出充值日志
          </Button>
        </>
      )}
    </Segment>
  );

  function renderSummaryTable() {
    const renderGroupValue = (row) => {
      if (isUserView) {
        return row.username;
      }
      if (isChannelView) {
        return row.channel_name
          ? `${row.channel_name} (#${row.channel_id})`
          : `#${row.channel_id}`;
      }
      return row.model_name;
    };
    const getRowKey = (row, index) => {
      if (isUserView) {
        return row.username || index;
      }
      if (isChannelView) {
        return row.channel_id;
      }
      return row.model_name || index;
    };

    return (
      <Table basic compact size='small'>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>{groupLabel}</Table.HeaderCell>
            <Table.HeaderCell>调用次数</Table.HeaderCell>
            <Table.HeaderCell>输入token</Table.HeaderCell>
            <Table.HeaderCell>输出token</Table.HeaderCell>
            <Table.HeaderCell>缓存token</Table.HeaderCell>
            <Table.HeaderCell>缓存创建token</Table.HeaderCell>
            <Table.HeaderCell>消耗额度</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {stats.map((row, index) => (
            <Table.Row key={getRowKey(row, index)}>
              <Table.Cell>{renderGroupValue(row)}</Table.Cell>
              <Table.Cell>{row.request_count}</Table.Cell>
              <Table.Cell>{formatTokenCount(row.prompt_tokens)}</Table.Cell>
              <Table.Cell>{formatTokenCount(row.completion_tokens)}</Table.Cell>
              <Table.Cell>{formatTokenCount(row.cached_tokens)}</Table.Cell>
              <Table.Cell>{formatTokenCount(row.cache_creation_tokens)}</Table.Cell>
              <Table.Cell>{renderQuota(row.quota, 4)}</Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
        {stats.length > 0 && (
          <Table.Footer>
            <Table.Row>
              <Table.HeaderCell><b>合计</b></Table.HeaderCell>
              <Table.HeaderCell>{totalRequests}</Table.HeaderCell>
              <Table.HeaderCell>{formatTokenCount(totalPrompt)}</Table.HeaderCell>
              <Table.HeaderCell>{formatTokenCount(totalCompletion)}</Table.HeaderCell>
              <Table.HeaderCell>{formatTokenCount(stats.reduce((s, r) => s + r.cached_tokens, 0))}</Table.HeaderCell>
              <Table.HeaderCell>{formatTokenCount(stats.reduce((s, r) => s + r.cache_creation_tokens, 0))}</Table.HeaderCell>
              <Table.HeaderCell>{renderQuota(totalQuota, 4)}</Table.HeaderCell>
            </Table.Row>
            {topupSummary && (
              <Table.Row>
                <Table.HeaderCell><b>充值合计</b></Table.HeaderCell>
                <Table.HeaderCell>{topupSummary.count} 笔</Table.HeaderCell>
                <Table.HeaderCell colSpan='4'></Table.HeaderCell>
                <Table.HeaderCell>{renderQuota(topupSummary.total_quota, 4)}</Table.HeaderCell>
              </Table.Row>
            )}
            {topupSummary && (
              <Table.Row>
                <Table.HeaderCell><b>兑换券合计</b></Table.HeaderCell>
                <Table.HeaderCell colSpan='5'></Table.HeaderCell>
                <Table.HeaderCell>{renderQuota(topupSummary.redemption_total, 4)}</Table.HeaderCell>
              </Table.Row>
            )}
            {topupSummary && (
              <Table.Row>
                <Table.HeaderCell><b>兑换券消耗合计</b></Table.HeaderCell>
                <Table.HeaderCell colSpan='5'></Table.HeaderCell>
                <Table.HeaderCell>{renderQuota(topupSummary.redemption_consumed, 4)}</Table.HeaderCell>
              </Table.Row>
            )}
          </Table.Footer>
        )}
      </Table>
    );
  }
};

export default ConsumptionSummary;
